import type { DailyStatusTodayDto } from "@/features/daily/types/daily.types";
import { commandViewModel, dailyFormat } from "@/features/daily/components/command/command-view-model";

type BaselineStatus = DailyStatusTodayDto["hourlyToday"]["baseline"];

export type TodayHourDeltaVm = { text: string; tone: "above" | "below" | "even" };

export type TodayHourVm = {
    /** Position in the served day: unique even when a DST change serves the same wall-clock hour twice. */
    slot: number;
    hour: number;
    label: string;
    range: string;
    tag: string | null;
    created: number;
    /** B18 (D136): the created split by type — the stacked bars; `null` when the answer does not say. */
    orders: number | null;
    deposits: number | null;
    completed: number;
    /** The baseline's expected creations for that hour; `null` without a baseline. */
    expected: number | null;
    /** The baseline's expected completions, rounded and, for the open hour, prorated to the minutes elapsed. */
    completedExpected: number | null;
    completedExpectedLabel: string;
    delta: TodayHourDeltaVm | null;
    footnotes: string[];
    quiet: boolean;
};

export type TodayHourlyVm = {
    hours: TodayHourVm[];
    /** Why no expected line is drawn; `null` when the baseline answered. */
    baselineNote: string | null;
};

export type TodayFunnelStepVm = {
    key: "created" | "funded" | "completed";
    label: string;
    countLabel: string;
    /** Share of the day's created orders, 0–1; `null` when nothing was created. */
    share: number | null;
    shareLabel: string;
};

export type TodayTypeFunnelVm = {
    key: "order" | "deposit";
    title: string;
    steps: TodayFunnelStepVm[];
    /** D146: the conversion itself — final for today; «—» with its reason below the minimum sample. */
    headlineLabel: string;
    headlineValue: string;
    tooltip: string[];
    /** D145: «+1 completada hoy que se creó antes» — what reconciles the funnel with the tile; `null` at 0. */
    createdBeforeNote: string | null;
};

/** D136: one funnel per type — orders and deposits are never averaged together. */
export type TodayFunnelVm = TodayTypeFunnelVm[];

export const CONVERSION_DEFINITION = "Conversión: operaciones con fondos recibidos sobre creadas · sin canceladas";

const BASELINE_NOTE: Record<BaselineStatus, string | null> = {
    ok: null,
    insufficient: "Sin semanas suficientes para la línea esperada",
    unavailable: "Línea esperada no disponible",
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

/** D24: the cache stores `unknown`; the page proves the shape instead of casting. */
export const isStatusTodayDto = (value: unknown): value is DailyStatusTodayDto =>
    isRecord(value) && isRecord(value.envelope) && isRecord(value.hourlyToday) && isRecord(value.todayFunnel) && Array.isArray(value.priorityOperations);

const createdBeforeNote = (count: number, singular: string, plural: string): string | null =>
    count === 0 ? null : count === 1 ? `+1 ${singular} hoy que se creó antes` : `+${dailyFormat.exactCount(count)} ${plural} hoy que se crearon antes`;

const shareOf = (count: number, created: number): number | null => (created === 0 ? null : count / created);

// The page's guard only proves the top level, so a split an older answer omits reads as unknown, never as 0.
const typeCount = (byType: unknown, key: "order" | "deposit"): number | null => {
    if (!isRecord(byType)) return null;
    const value = byType[key];
    return typeof value === "number" ? value : null;
};

const pad = (value: number): string => String(value).padStart(2, "0");

const MINUS = "\u2212";
const HOUR_MS = 3_600_000;
const MINUTE_MS = 60_000;
const PRORATE_NOTE = "Hora sin cerrar: lo esperado se prorratea a los minutos transcurridos.";

// Against the same rounded figure the panel shows, so the difference always adds up on screen.
const hourDelta = (completed: number, target: number | null): TodayHourDeltaVm | null => {
    if (target === null) return null;
    const difference = completed - target;
    if (difference === 0) return { text: "En línea con lo esperado", tone: "even" };
    const sign = difference > 0 ? "+" : MINUS;
    const percent = target === 0 ? "" : ` (${sign}${Math.round((Math.abs(difference) / target) * 100)} %)`;
    const side = difference > 0 ? "por encima" : "por debajo";
    return { text: `${sign}${dailyFormat.exactCount(Math.abs(difference))}${percent} ${side} de lo esperado`, tone: difference > 0 ? "above" : "below" };
};

export const todayViewModel = {
    /**
     * D134: created (bars), completed (line) and the baseline's expected creations, hour by hour. `now` is the
     * instant the hours were cut at; the last hour served is the one still running (the server only returns hours
     * up to now), so its expected completions are prorated to the minutes elapsed, as the design does.
     */
    hourly: (dto: DailyStatusTodayDto, now: number): TodayHourlyVm => {
        const baselineNote = BASELINE_NOTE[dto.hourlyToday.baseline];
        // Madrid is a whole-hour offset from UTC, so the minute within the hour is the same in both.
        const minutes = Math.floor((now % HOUR_MS) / MINUTE_MS);
        return {
            hours: dto.hourlyToday.hours.map((hour, index, all) => {
                const open = index === all.length - 1;
                const completedExpected = hour.expectedCompleted === null
                    ? null
                    : Math.round(open ? (hour.expectedCompleted * minutes) / 60 : hour.expectedCompleted);
                const expectedLabel = open ? `Esperadas a las ${pad(hour.hour)}:${pad(minutes)}` : "Esperadas";
                return {
                    slot: index,
                    hour: hour.hour,
                    label: `${pad(hour.hour)}h`,
                    range: `${pad(hour.hour)}:00–${pad(hour.hour)}:59`,
                    tag: open ? "hora en curso" : hour.quiet === true ? "hora tranquila" : null,
                    created: hour.created,
                    orders: typeCount(hour.createdByType, "order"),
                    deposits: typeCount(hour.createdByType, "deposit"),
                    completed: hour.completed,
                    expected: hour.expectedCreated,
                    completedExpected,
                    completedExpectedLabel: baselineNote === null ? expectedLabel : "Sin baseline",
                    delta: hourDelta(hour.completed, completedExpected),
                    footnotes: [...(open ? [PRORATE_NOTE] : []), ...(baselineNote === null ? [] : [baselineNote])],
                    quiet: hour.quiet === true,
                };
            }),
            baselineNote,
        };
    },

    /**
     * D134: created → with funds → completed; each step as a share of the created. D146:
     * the conversion has no maturation any more, so today's figure is final and headlines
     * each funnel (the server flags it `insufficient` below the minimum sample).
     */
    funnel: (dto: DailyStatusTodayDto): TodayFunnelVm => {
        const { order, deposit } = dto.todayFunnel;
        const step = (key: TodayFunnelStepVm["key"], label: string, count: number, created: number): TodayFunnelStepVm => {
            const share = shareOf(count, created);
            return { key, label, countLabel: dailyFormat.exactCount(count), share, shareLabel: dailyFormat.kpiPercent(share, 1) };
        };
        const funnels: TodayFunnelVm = [
            {
                key: "order",
                title: "Órdenes",
                steps: [
                    step("created", "Creadas", order.created, order.created),
                    step("funded", "Con fondos", order.funded, order.created),
                    step("completed", "Completadas", order.completed, order.created),
                ],
                headlineLabel: "Conversión",
                headlineValue: dailyFormat.kpiPercent(order.conversion.value, 1),
                tooltip: [CONVERSION_DEFINITION, ...commandViewModel.reasonTexts(order.conversion.reasonCodes)],
                createdBeforeNote: createdBeforeNote(order.completedTodayCreatedBefore, "completada", "completadas"),
            },
            {
                key: "deposit",
                title: "Depósitos",
                steps: [
                    step("created", "Creados", deposit.created, deposit.created),
                    step("funded", "Recibidos", deposit.received, deposit.created),
                    step("completed", "Acreditados", deposit.credited, deposit.created),
                ],
                headlineLabel: "Conversión",
                headlineValue: dailyFormat.kpiPercent(deposit.conversion.value, 1),
                tooltip: ["Conversión: depósitos con el dinero recibido sobre los creados · sin cancelados", ...commandViewModel.reasonTexts(deposit.conversion.reasonCodes)],
                createdBeforeNote: createdBeforeNote(deposit.completedTodayCreatedBefore, "acreditado", "acreditados"),
            },
        ];
        // D139: a type with nothing created today is not drawn beside one that has activity.
        const active = funnels.filter((funnel) => (funnel.key === "order" ? order.created : deposit.created) !== 0);
        return active.length === 0 ? funnels : active;
    },
};
